import React, { useState } from "react";
import { Stack } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";

import invariant from "tiny-invariant";

import IndividualTournamentSidebarButtons from "./IndividualTournamentSidebarButtons";
import PlayoffButtonsModal from "./PlayoffButtonsModal";

import { tournamentManagementActions } from "../../features/tourneymgmt/tournamentManagementSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { Breakpoint } from "../../hooks/useBreakpoint";
import { AUTH_LEVELS } from "../../lib/globals";

const TournamentManagementSidebar = ({
    breakpoint,
    toggleNavbar,
    horizontal = false,
}: {
    breakpoint: Breakpoint;
    toggleNavbar: () => void;
    horizontal?: boolean;
}): React.JSX.Element => {
    const { user } = useAppSelector(state => state.auth);
    invariant(user);
    const location = useLocation();
    const dispatch = useAppDispatch();
    let tournament: number | undefined;

    const routePrefix = "/tournament-management";
    const [showModal, setShowModal] = useState<boolean>(false);
    const [modalTournamentId, setModalTournamentId] = useState<number>();
    const [modalAction, setModalAction] = useState<
        | "assign"
        | "reset"
        | "pool"
        | "clear"
        | "placeholder"
        | "clearplaceholder"
    >("assign");

    const tournamentPattern = /tournament-management\/tournament\/(\d+)/;
    const isOnTournamentPage = tournamentPattern.test(location.pathname);
    const match = tournamentPattern.exec(location.pathname);

    const state = useAppSelector(state => state.tournamentmanagement);

    if (!state.selectedTournament) {
        if (match) {
            tournament = parseInt(match![1]);
            dispatch(
                tournamentManagementActions.setselectedTournamentId(tournament),
            );
        } else {
            tournament = undefined;
        }
    } else {
        tournament = state.selectedTournament;
    }

    return (
        <>
            <Stack
                gap={2}
                direction={horizontal ? "horizontal" : "vertical"}
                className={`d-flex ${horizontal ? "flex-wrap justify-content-center" : ""}`}
            >
                <Link
                    to={`${routePrefix}`}
                    className="btn btn-primary"
                    onClick={toggleNavbar}
                >
                    All Tournaments
                </Link>
                {isOnTournamentPage && tournament && (
                    <>
                        <IndividualTournamentSidebarButtons
                            breakpoint={breakpoint}
                            tournament={tournament}
                            routePrefix={routePrefix}
                            setModalAction={setModalAction}
                            setModalTournamentId={setModalTournamentId}
                            setShowModal={setShowModal}
                            toggleNavbar={toggleNavbar}
                        />
                        <hr />
                    </>
                )}
                {AUTH_LEVELS.sitemanagement.includes(user.userLevel) && (
                    <React.Fragment>
                        <Link
                            to={`${routePrefix}/teams-by-tournament`}
                            className="btn btn-primary"
                            onClick={toggleNavbar}
                        >
                            Teams by Tournament
                        </Link>
                        <Link
                            to={`${routePrefix}/templates`}
                            className="btn btn-primary"
                            onClick={toggleNavbar}
                        >
                            Schedule Templates
                        </Link>
                        <Link
                            to={`${routePrefix}/organizations`}
                            className="btn btn-primary"
                            onClick={toggleNavbar}
                        >
                            Organizations
                        </Link>
                        <Link
                            to={`${routePrefix}/coiApproval`}
                            className="btn btn-primary"
                            onClick={toggleNavbar}
                        >
                            COI Approvals
                        </Link>
                        <Link
                            to={`${routePrefix}/fields`}
                            className="btn btn-primary"
                            onClick={toggleNavbar}
                        >
                            Field Locations
                        </Link>
                        <Link
                            to={`${routePrefix}/homeLeagues`}
                            className="btn btn-primary"
                            onClick={toggleNavbar}
                        >
                            Home Leagues
                        </Link>
                    </React.Fragment>
                )}
                <Link
                    to={`${routePrefix}/game-logs`}
                    className="btn btn-primary"
                    onClick={toggleNavbar}
                >
                    Game Logs
                </Link>
                {AUTH_LEVELS.sitemanagement.includes(user.userLevel) && (
                    <React.Fragment>
                        <Link
                            to={`${routePrefix}/tournament-registrations`}
                            className="btn btn-primary"
                            onClick={toggleNavbar}
                        >
                            Tournament Registrations
                        </Link>
                        <Link
                            to={`${routePrefix}/products`}
                            className="btn btn-primary"
                            onClick={toggleNavbar}
                        >
                            Product Management
                        </Link>
                        <Link
                            to={`${routePrefix}/promo-codes`}
                            className="btn btn-primary"
                            onClick={toggleNavbar}
                        >
                            Promo Codes
                        </Link>
                    </React.Fragment>
                )}
            </Stack>
            <PlayoffButtonsModal
                action={modalAction}
                tournamentId={modalTournamentId as number}
                showModal={showModal}
                setShowModal={setShowModal}
            />
        </>
    );
};

export default TournamentManagementSidebar;
